import { useState, useEffect, useRef } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { Space, Select, Input, Flex, Modal, Popover, Button } from "antd";

import {
  AimOutlined,
  FormOutlined,
  SearchOutlined,
  DeleteOutlined,
  DownOutlined,
  UpOutlined,
  CopyOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

import { v4 as uuidv4 } from "uuid";

import "./styles.scss";
import { ActionButton } from "../../../components/elements/Button";
import { StepBlockWrapper } from "../StepBlockWrapper";

const generateUniqueId = () => uuidv4();

// Generic info icon used inline next to form labels. Antd's
// InfoCircleOutlined keeps the look consistent with the rest of the portal
// (TagMonitor, TagMonitorConfig, JsonObjTable all use the same family).
export const InfoCircle = () => (
  <InfoCircleOutlined
    aria-label="info"
    style={{ color: "#8c8c8c", fontSize: 14, marginLeft: 4 }}
  />
);

const defaultStepCellStyle = {
  position: "relative",
  padding: "1rem",
  backgroundColor: "#fff",
  border: "1px solid rgba(219, 241, 255, 1)",
  borderRadius: "3px",
  // boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  cursor: "grab",
};

const stepTypes = {
  "": "Select action type",
  click_on_element: "Click on element",
  click_on_random_element: "Click on random element from list",
  collect_dl_object: "Collect dataLayer object",
  collect_dl_before_navigation: "Collect dataLayer before navigation",
  check_cookie_value: "Check cookie value",
  date_range_select: "Select date range",
  fill_input_field: "Fill input field",
  go_to_page: "Go to page",
  // pick_random_element_from_list: "Pick random element from list",
  scroll_to_percentage: "Scroll to depth",
  select_option: "Select dropdown menu option",
  wait_until_element_visible: "Wait until element appear",
};

const stepParamDescriptions = {
  action_type: "Define an action type that has to be executed on the page.",
  delay: "A time delay after action complete (in seconds).",
  optional:
    "Wether current step is optional and should not interrupt the execution (might be useful for elements that appears in unique cases).",
  wait_for_page_load: "Wait for a page load after completing the action.",
  opens_new_page: "Move execution to the new page opened by the action.",
  element_selector: "Query selector of the element.",
  value: "Fill the input value with following text.",
  go_to_page: "The URL is the link of the page that you want to visit.",
  fill_input_field:
    "Fill in the value or text that is desired in the input field.",
  datalayer_obj_to_find:
    "Template of the DataLayer object to find on the page.",
  cookie_value_checks:
    "Define which cookies to check and the value each one is expected to have.",
  scroll_to_percentage:
    "Scrolls the page down to a set depth so scroll-depth tracking fires. Thresholds fire once per page load and do not re-arm, so order scroll steps low to high and put each one before the step that collects it. Adding a scroll step also disables the automatic 500px nudge on this scenario's dataLayer collection steps, so lazy-loaded content has to be reached by an explicit scroll.",
};

const defaultStepObject = () => ({
  step: null,
  stepId: generateUniqueId(),
  delay: 0,
  type: null,
  selector: null,
  optional: false,
  waitForNavigation: false,
  opensNewPage: false,
  dataLayerObjToFind: null,
  value: null,
  calendarSel: "",
  arrowSel: "",
  daysSel: "",
  availableDaysClass: "",
  minDaysNeeded: 1,
});

// Sortable item component
function SortableItem(props) {
  const {
    step = {},
    index = 0,
    isCollapsed,
    allowedStepTypes = null,
    handleDataLayerObjEdit = () => {},
    handleStepEdit = () => {},
    handleDeleteStepClick = () => {},
    handleCopyStepClick = () => {},
    handleToggleStepClick = () => {},
  } = props;

  // When the parent restricts the available step types (e.g. cookies /
  // login only allow "click_on_element"), filter the dropdown accordingly.
  // `null` / empty array = unrestricted.
  const visibleStepTypes = (() => {
    if (!Array.isArray(allowedStepTypes) || allowedStepTypes.length === 0) {
      return stepTypes;
    }
    const filtered = { "": "Select action type" };
    allowedStepTypes.forEach((key) => {
      if (stepTypes[key] != null) filtered[key] = stepTypes[key];
    });
    return filtered;
  })();

  const [stepId, setStepId] = useState(step?.stepId || "");
  const [stepType, setStepType] = useState(step?.type || "");
  const [stepSelector, setStepSelector] = useState(step?.selector || "");
  const [stepDelay, setStepDelay] = useState(step?.delay || 0);
  const [stepOptional, setStepOptional] = useState(!!step?.optional);
  const [stepWaitForPageLoad, setWaitForPageLoad] = useState(
    !!step?.waitForNavigation
  );
  const [stepOpensNewPage, setStepOpensNewPage] = useState(
    !!step?.opensNewPage
  );
  const [stepDataLayerObj, setStepDataLayerObj] = useState(
    step?.dataLayerObjToFind || []
  );
  const [stepValue, setStepValue] = useState(step?.value);

  const [stepCalendarSel, setStepCalendarSel] = useState(
    step?.calendarSel || ""
  );
  const [stepArrowSel, setStepArrowSel] = useState(step?.arrowSel || "");
  const [stepDaysSel, setStepDaysSel] = useState(step?.daysSel || "");
  const [stepAvailableDaysClassSel, setStepAvailableDaysClassSel] = useState(
    step?.availableDaysClass || ""
  );
  const [stepMinDaysNeededSel, setStepMinDaysNeededSel] = useState(
    step?.minDaysNeeded || 1
  );

  // When there's only one valid step type, auto-select it so the user
  // doesn't have to open the dropdown for a single-option choice.
  useEffect(() => {
    if (
      (!stepType || stepType === "") &&
      Array.isArray(allowedStepTypes) &&
      allowedStepTypes.length === 1
    ) {
      setStepType(allowedStepTypes[0]);
    }
  }, [allowedStepTypes, stepType]);

  // Field labels
  const [stepValueLabel, setStepValueLabel] = useState();
  useEffect(() => {
    switch (stepType) {
      case "go_to_page":
        setStepValueLabel("Page address");
        break;
      case "collect_dl_object":
      case "collect_dl_before_navigation":
        setStepValueLabel("Event name");
        break;
      default:
        setStepValueLabel("Input field value");
    }
  }, [stepType]);

  // Seed a depth the first time a step is switched to scroll. defaultStepObject
  // is shared by every type, so the default can't live there.
  useEffect(() => {
    if (
      stepType === "scroll_to_percentage" &&
      (stepValue === null || stepValue === undefined || stepValue === "")
    ) {
      setStepValue(50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepType]);

  // A depth saved outside the preset list (hand-edited config) still shows: antd
  // falls back to rendering the raw value, so it survives an unrelated edit.
  const scrollDepthSelectValue =
    stepValue === null || stepValue === undefined || stepValue === ""
      ? undefined
      : Number(stepValue);

  // The action parseFloats this, so store a number rather than the option string.
  const handleScrollDepthChange = (percent) => setStepValue(Number(percent));

  // useSortable hook
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: step.stepId,
      key: step.stepId,
    });

  const style = {
    transform: CSS.Transform.toString(transform ? { ...transform, scaleX: 1, scaleY: 1 } : null),
    transition: transition || undefined,
    width: "100%",
    ...defaultStepCellStyle,
  };

  // Skip edit propagation on mount and after syncing from parent prop.
  const skipEditRef = useRef(true);
  // Track whether the parent update was caused by our own propagation (echo).
  const isLocalUpdateRef = useRef(false);
  // Track whether SortableItem has completed its initial mount sync
  const isMountedRef = useRef(false);

  useEffect(() => {
    if (skipEditRef.current) {
      skipEditRef.current = false;
      return;
    }

    const newStepData = {
      ...step,
      step: index,
      type: stepType,
      selector: stepSelector,
      delay: stepDelay,
      optional: stepOptional,
      waitForNavigation: stepWaitForPageLoad,
      opensNewPage: stepOpensNewPage,
      dataLayerObjToFind: stepDataLayerObj,
      value: stepValue,
      calendarSel: stepCalendarSel,
      arrowSel: stepArrowSel,
      daysSel: stepDaysSel,
      availableDaysClass: stepAvailableDaysClassSel,
      minDaysNeeded: stepMinDaysNeededSel,
    };

    isLocalUpdateRef.current = true;
    handleStepEdit(stepId, newStepData);
  }, [
    stepType,
    stepSelector,
    stepDelay,
    stepOptional,
    stepWaitForPageLoad,
    stepOpensNewPage,
    stepDataLayerObj,
    stepValue,
    stepCalendarSel,
    stepArrowSel,
    stepDaysSel,
    stepAvailableDaysClassSel,
    stepMinDaysNeededSel,
  ]);

  useEffect(() => {
    // On initial mount: sync state but do NOT re-arm skipEditRef.
    // Re-arming on mount is what caused the first user keystroke to be dropped.
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      setStepId(step?.stepId);
      setStepType(step?.type);
      setStepSelector(step?.selector);
      setStepDelay(step?.delay);
      setStepOptional(step?.optional);
      setWaitForPageLoad(step?.waitForNavigation);
      setStepOpensNewPage(step?.opensNewPage);
      setStepDataLayerObj(step?.dataLayerObjToFind || []);
      setStepValue(step?.value);
      setStepCalendarSel(step?.calendarSel);
      setStepArrowSel(step?.arrowSel);
      setStepDaysSel(step?.daysSel);
      setStepAvailableDaysClassSel(step?.availableDaysClass);
      setStepMinDaysNeededSel(step?.minDaysNeeded);
      return;
    }
    // Skip sync when the prop change is just our own edit echoing back.
    if (isLocalUpdateRef.current) {
      isLocalUpdateRef.current = false;
      return;
    }
    // Genuine external prop change: re-arm skip and sync state
    skipEditRef.current = true;
    setStepId(step?.stepId);
    setStepType(step?.type);
    setStepSelector(step?.selector);
    setStepDelay(step?.delay);
    setStepOptional(step?.optional);
    setWaitForPageLoad(step?.waitForNavigation);
    setStepOpensNewPage(step?.opensNewPage);
    setStepDataLayerObj(step?.dataLayerObjToFind || []);
    setStepValue(step?.value);
    setStepCalendarSel(step?.calendarSel);
    setStepArrowSel(step?.arrowSel);
    setStepDaysSel(step?.daysSel);
    setStepAvailableDaysClassSel(step?.availableDaysClass);
    setStepMinDaysNeededSel(step?.minDaysNeeded);
  }, [step]);

  return (
    <Flex align="start" style={{ width: "100%" }} gap={38}>
      {/* Step number badge */}
      <div className="scenario-step-number">{index}</div>

      {/* The card */}
      <div
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        style={{ ...style, flexDirection: "column", width: "100%" }}
      >
        {/* Top‐right icon row */}
        <Space
          size={8}
          style={{
            position: "absolute",
            top: "1rem",
            right: "1rem",
            zIndex: 2,
          }}
        >
          <CopyOutlined
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => handleCopyStepClick(step.stepId)}
            style={{ cursor: "pointer" }}
          />
          <DeleteOutlined
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => handleDeleteStepClick(step.stepId)}
            style={{ cursor: "pointer" }}
          />
          {isCollapsed ? (
            <DownOutlined
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => handleToggleStepClick(step.stepId)}
              style={{ cursor: "pointer" }}
            />
          ) : (
            <UpOutlined
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => handleToggleStepClick(step.stepId)}
              style={{ cursor: "pointer" }}
            />
          )}
        </Space>

        {/* Main form fields */}
        {/* show the summary header when collapsed */}
        {isCollapsed && (
          <div>{stepType ? stepTypes[stepType] : "No action selected…"}</div>
        )}
        {/* Show the full form when not collapsed */}
        {!isCollapsed && (
          <Space direction="vertical" size={20} style={{ width: "100%" }}>
            {/* Type of action */}
            <Popover
              placement="topLeft"
              content={stepParamDescriptions.action_type}
            >
              <Space size={5} align="center">
                <label className="scenario-step-label">
                  Type of (inter) action
                </label>
                <InfoCircle />
              </Space>
            </Popover>
            <Select
              className="scenario-step-select"
              options={Object.keys(visibleStepTypes).map((key) => ({
                value: key,
                label: visibleStepTypes[key],
                disabled: key === "",
              }))}
              value={stepType}
              placeholder="Select action type"
              onChange={(value) => setStepType(value)}
              popupMatchSelectWidth={false}
              onPointerDown={(e) => e.stopPropagation()}
              style={{ height: 30 }}
            />

            {/* Element selector (for most stepTypes) */}
            {![
              "collect_dl_object",
              "go_to_page",
              "date_range_select",
              "check_cookie_value",
              "scroll_to_percentage",
            ].includes(stepType) && (
              <Space size={5} direction="vertical">
                <Popover
                  placement="topLeft"
                  content={stepParamDescriptions.element_selector}
                >
                  <Space size={5} align="center">
                    <label className="scenario-step-label">
                      Element selector
                    </label>
                    <InfoCircle />
                  </Space>
                </Popover>
                <Input
                  name="step_selector"
                  className="scenario-step-select"
                  value={stepSelector}
                  placeholder="Element selector"
                  onChange={(e) => setStepSelector(e.target.value)}
                  onBlur={(e) => { if (e.target.value !== stepSelector) setStepSelector(e.target.value); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={{ minWidth: 220, height: 30 }}
                />
              </Space>
            )}

            {/* Scroll depth (for scroll_to_percentage) */}
            {stepType === "scroll_to_percentage" && (
              <Space size={5} direction="vertical">
                <Popover
                  placement="topLeft"
                  content={stepParamDescriptions.scroll_to_percentage}
                >
                  <Space size={5} align="center">
                    <label className="scenario-step-label">
                      Scroll depth (%)
                    </label>
                    <InfoCircle />
                  </Space>
                </Popover>
                <Select
                  className="scenario-step-select"
                  value={scrollDepthSelectValue}
                  placeholder="Select a depth"
                  options={[25, 50, 75, 100].map((percent) => ({
                    value: percent,
                    label: `${percent}%`,
                  }))}
                  onChange={handleScrollDepthChange}
                  popupMatchSelectWidth={false}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={{ minWidth: 220, height: 30 }}
                />
                <div style={{ color: "#8c8c8c", fontSize: 12, maxWidth: 320 }}>
                  These match GTM's default scroll thresholds. Check the
                  client's container if it uses custom ones.
                </div>
              </Space>
            )}

            {/* Input field (for go_to_page and fill_input_field) */}
            {["fill_input_field"].includes(stepType) && (
              <Space size={5} direction="vertical">
                <Popover
                  placement="topLeft"
                  content={stepParamDescriptions.fill_input_field}
                >
                  <Space size={5} align="center">
                    <label className="scenario-step-label">Field input</label>
                    <InfoCircle />
                  </Space>
                </Popover>
                <Input
                  name="step_value"
                  className="scenario-step-select"
                  value={stepValue}
                  placeholder="Value or text to enter in the field"
                  onChange={(e) => setStepValue(e.target.value)}
                  onBlur={(e) => { if (e.target.value !== stepValue) setStepValue(e.target.value); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{ minWidth: 220, height: 30 }}
                />
              </Space>
            )}

            {["go_to_page"].includes(stepType) && (
              <Space size={5} direction="vertical">
                <Popover
                  placement="topLeft"
                  content={stepParamDescriptions.go_to_page}
                >
                  <Space size={5} align="center">
                    <label className="scenario-step-label">Page URL</label>
                    <InfoCircle />
                  </Space>
                </Popover>
                <Input
                  className="scenario-step-select"
                  name="step_value"
                  value={stepValue}
                  placeholder="URL"
                  onChange={(e) => setStepValue(e.target.value)}
                  onBlur={(e) => { if (e.target.value !== stepValue) setStepValue(e.target.value); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={{ minWidth: 220, height: 30 }}
                />
              </Space>
            )}

            {/* DataLayer object edit (for collect_dl_object / collect_dl_before_navigation) */}
            {[
              "collect_dl_object",
              "collect_dl_before_navigation",
              "check_cookie_value",
            ].includes(stepType) && (
              <Space size={5} direction="vertical">
                <Popover
                  placement="topLeft"
                  content={
                    stepType === "check_cookie_value"
                      ? stepParamDescriptions.cookie_value_checks
                      : stepParamDescriptions.datalayer_obj_to_find
                  }
                >
                  <Space size={5} align="center">
                    <SearchOutlined />
                    <label className="scenario-step-label">
                      {stepType === "check_cookie_value"
                        ? "Cookie value checks"
                        : "DataLayer object to find"}
                    </label>
                  </Space>
                </Popover>
                <ActionButton
                  type="primary"
                  text={
                    <Space size={10}>
                      <FormOutlined />{" "}
                      {stepType === "check_cookie_value"
                        ? "Edit cookie checks"
                        : "Edit object template"}
                    </Space>
                  }
                  height={30}
                  width={200}
                  onClickFunction={() =>
                    handleDataLayerObjEdit(stepDataLayerObj, stepId, stepType)
                  }
                />
              </Space>
            )}

            {/* Date-range selector fields */}
            {stepType === "date_range_select" && (
              <>
                {[
                  {
                    label: "Calendar element",
                    value: stepCalendarSel,
                    setter: setStepCalendarSel,
                    name: "step_calendar_selector",
                  },
                  {
                    label: "Arrow element",
                    value: stepArrowSel,
                    setter: setStepArrowSel,
                    name: "step_calendar_arrow",
                  },
                  {
                    label: "Days",
                    value: stepDaysSel,
                    setter: setStepDaysSel,
                    name: "step_days_selector",
                  },
                  {
                    label: "Available days",
                    value: stepAvailableDaysClassSel,
                    setter: setStepAvailableDaysClassSel,
                    name: "step_available_days_selector",
                  },
                ].map(({ label, value, setter, name }) => (
                  <Space key={name} size={5} direction="vertical">
                    <Popover placement="topLeft" content="">
                      <Space size={5} align="center">
                        <AimOutlined />
                        <label className="scenario-step-label">{label}</label>
                      </Space>
                    </Popover>
                    <Input
                      name={name}
                      value={value}
                      placeholder={`${label} selector`}
                      onChange={(e) => setter(e.target.value)}
                      onBlur={(e) => { if (e.target.value !== value) setter(e.target.value); }}
                      onPointerDown={(e) => e.stopPropagation()}
                      style={{ maxWidth: 135, height: 30 }}
                    />
                  </Space>
                ))}

                {/* Days to select (numeric) */}
                <Space size={5} direction="vertical">
                  <Popover placement="topLeft" content="">
                    <Space size={5} align="center">
                      <AimOutlined />
                      <label className="scenario-step-label">
                        Days to select
                      </label>
                    </Space>
                  </Popover>
                  <Input
                    name="step_calendar_min_days"
                    value={stepMinDaysNeededSel}
                    type="number"
                    defaultValue={0}
                    max={30}
                    min={0}
                    onChange={(e) => setStepMinDaysNeededSel(e.target.value)}
                    onPointerDown={(e) => e.stopPropagation()}
                    style={{ maxWidth: 135, height: 30 }}
                  />
                </Space>
              </>
            )}
          </Space>
        )}
      </div>
    </Flex>
  );
}

// Sortable list component
export const SortableList = (props) => {
  const {
    initialItems,
    items = [],
    allowedStepTypes = null,
    handleDataLayerObjEdit = () => {},
    handleStepsUpdate = () => {},
    resolveComponentSteps = () => [],
    resolveLinkedSteps = () => [],
    getBlockDisplayName = () => null,
    onDetachBlock = () => {},
    onDeleteBlock = () => {},
    onEditSource = () => {},
  } = props;

  // Initialize state for items
  const [curItems, setCurItems] = useState(items);
  const [stepToDeleteId, setStepToDeleteId] = useState(null);
  // local copy of your steps
  const [steps, setSteps] = useState(initialItems);
  // track which steps are collapsed
  const [collapsedMap, setCollapsedMap] = useState({});

  // Steps list editing
  const [stepRemoveConfirmation, setStepRemoveConfirmation] = useState(false);

  // Delete handler: deletes the clicked step right after it
  const handleDeleteStepClick = (id) => {
    setStepRemoveConfirmation(true);
    setStepToDeleteId(id);
  };
  const handleDeleteStepConfirmation = () => {
    setStepRemoveConfirmation(false);
    handleStepDelete();
  };
  const handleDeleteStepCancel = () => {
    setStepRemoveConfirmation(false);
    setStepToDeleteId(null);
  };

  // Copy handler: duplicates the clicked step right after it
  const handleCopyStepClick = (stepId) => {
    setCurItems((prev) => {
      const idx = prev.findIndex((s) => s.stepId === stepId);
      if (idx === -1) return prev;

      const original = prev[idx];
      const clone = { ...original, stepId: generateUniqueId() };

      const newItems = [
        ...prev.slice(0, idx + 1), // everything up to and including original
        clone, // the new copy
        ...prev.slice(idx + 1), // the rest
      ];

      handleStepsUpdate(newItems);
      return newItems;
    });
  };

  // Toggle handler: flips collapsed state
  const handleToggleStepClick = (stepId) => {
    setCollapsedMap((prev) => {
      const next = { ...prev, [stepId]: !prev[stepId] };
      return next;
    });
  };

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get the sortable ID for an item (step or block)
  const getItemId = (item) => item?.blockType ? item.blockId : item.stepId;

  // Handler when drag ends
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active?.id && over?.id && active.id !== over.id) {
      setCurItems((prevItems) => {
        const oldIndex = prevItems.findIndex((i) => getItemId(i) === active.id);
        const newIndex = prevItems.findIndex((i) => getItemId(i) === over.id);
        const newOrder = arrayMove(prevItems, oldIndex, newIndex);

        // **Tell the parent about the new order so it “sticks”**
        handleStepsUpdate(newOrder);

        return newOrder;
      });
    }
  };

  const handleStepAdd = () => setCurItems([...curItems, defaultStepObject()]);

  const handleStepEdit = (id, step) => {
    const newItemsList = curItems.map((elem) => {
      switch (id === elem.stepId) {
        case true:
          return step;
        default:
          return elem;
      }
    });

    handleStepsUpdate(newItemsList);
  };

  const handleStepDelete = () => {
    const newItemsList =
      curItems.filter((elem) => elem.stepId !== stepToDeleteId) || [];
    handleStepsUpdate(newItemsList);
  };

  // Updating data
  useEffect(() => setCurItems(items), [items]);

  return (
    <div className="sortable-list-container">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={curItems.map((item) => getItemId(item))}
          strategy={verticalListSortingStrategy}
        >
          <Space direction="vertical">
            {curItems.length
              ? (() => {
                  let stepCounter = 1;
                  return curItems.map((item) => {
                    if (item?.blockType) {
                      // Resolve steps for this block. The resolvers return
                      // `null` when the source doesn't exist (stale ref) and
                      // an array (possibly empty) when it does.
                      let resolvedSteps = [];
                      let isStale = false;

                      if (item.blockType === "component_ref") {
                        const resolved = resolveComponentSteps(item.componentId);
                        isStale = resolved === null;
                        resolvedSteps = resolved || [];
                      } else if (item.blockType === "scenario_link") {
                        const resolved = resolveLinkedSteps(
                          item.sourceScenarioId,
                          item.sourceStepsType,
                        );
                        isStale = resolved === null;
                        resolvedSteps = resolved || [];
                      }

                      const startIndex = stepCounter;
                      stepCounter += resolvedSteps.length || 1;

                      return (
                        <div
                          key={item.blockId}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            marginBottom: 24,
                            width: "100%",
                          }}
                        >
                          <Flex align="start" style={{ width: "100%" }} gap={38}>
                            <div className="scenario-step-number">{startIndex}</div>
                            <StepBlockWrapper
                              block={item}
                              resolvedSteps={resolvedSteps}
                              startIndex={startIndex}
                              isStale={isStale}
                              liveName={getBlockDisplayName(item)}
                              onDetach={onDetachBlock}
                              onDelete={onDeleteBlock}
                              onEditSource={onEditSource}
                            />
                          </Flex>
                        </div>
                      );
                    }

                    // Regular step
                    const currentIndex = stepCounter;
                    stepCounter++;

                    return (
                      <div
                        key={item.stepId}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          marginBottom: 24,
                          width: "100%",
                        }}
                      >
                        <SortableItem
                          key={item.stepId}
                          index={currentIndex}
                          step={item}
                          allowedStepTypes={allowedStepTypes}
                          handleDataLayerObjEdit={handleDataLayerObjEdit}
                          handleStepEdit={handleStepEdit}
                          handleCopyStepClick={handleCopyStepClick}
                          handleDeleteStepClick={handleDeleteStepClick}
                          handleToggleStepClick={handleToggleStepClick}
                          isCollapsed={!!collapsedMap[item.stepId]}
                        />
                      </div>
                    );
                  });
                })()
              : undefined}
            <Space size="large" style={{ marginTop: 16 }}>
              <Button type="default" onClick={handleStepAdd}>
                + add step
              </Button>
            </Space>
          </Space>
        </SortableContext>
      </DndContext>

      <Modal
        centered
        open={stepRemoveConfirmation}
        title="Confirm"
        onOk={() => handleDeleteStepConfirmation()}
        onCancel={() => handleDeleteStepCancel()}
        footer={(_, { OkBtn, CancelBtn }) => (
          <Flex gap={5}>
            <CancelBtn />
            <OkBtn />
          </Flex>
        )}
      >
        <p>Do you want to delete this step?</p>
      </Modal>
    </div>
  );
};
